/**
 * Pure derivation helpers for TPX-scoped user permissions.
 *
 * All matching is case-insensitive + trimmed to stay resilient to minor
 * API casing drift (see spec `docs/TPX_APPLICATION_PERMISSIONS_SPEC.md`).
 *
 * These helpers are framework-agnostic so they can be unit-tested in
 * isolation and reused from a context, a hook, or an imperative service.
 */
import {
  TPX_APPLICATION_ID,
  // TPX_PERMISSION_NAMES,
  // TPX_PERMISSION_VALUES,
  TPX_PERMISSION
} from "../constants/tpxApplication";

const norm = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

/**
 * Filter roles down to those that belong to the TPX application.
 * Roles without an `application`, or with a different `application.id`,
 * are ignored for TPX permission checks.
 *
 * @param {Array<{application?: {id?: number}}>} roles
 * @returns {Array}
 */
export const getTpxRoles = (roles) => {
  if (!Array.isArray(roles)) return [];
  return roles.filter(
    (role) => role && role.application && role.application.id === TPX_APPLICATION_ID
  );
};

/**
 * Returns true if any TPX role exposes a permission matching `name` + `value`.
 *
 * @param {Array} roles raw user roles
 * @param {string} name permission name (e.g. "Plan")
 * @param {string} value permission value (e.g. "View", "Edit")
 * @returns {boolean}
 */
export const hasTpxPermission = (roles, name, value) => {
  const tpxRoles = getTpxRoles(roles);
  if (tpxRoles.length === 0) return false;
  const targetName = norm(name);
  const targetValue = norm(value);
  return tpxRoles.some((role) =>
    (role.permissions || []).some(
      (p) => norm(p?.name) === targetName && norm(p?.value) === targetValue
    )
  );
};

/**
 * Derive the effective Plan-module permissions.
 *
 * View and Edit are independent: `planEdit` does **not** imply `planView`.
 * Sidebar and Plans routes require **`planView`** (see app guards).
 *
 * Multiple TPX roles are unioned (any grant wins).
 *
 * @param {Array} roles
 * @returns {{ planView: boolean, planEdit: boolean,  planCreate : boolean, planDelete : boolean}}
 */
export const getPlanPermissions = (user) => {
  const planView = user?.tpxPermissions?.includes(TPX_PERMISSION.planView);
  const planEdit = user?.tpxPermissions?.includes(TPX_PERMISSION.planEdit);
  const planCreate = user?.tpxPermissions?.includes(TPX_PERMISSION.planCreate);
  const planDelete = user?.tpxPermissions?.includes(TPX_PERMISSION.planDelete);

  return {
    planView,
    planEdit,
    planCreate,
    planDelete
  };
};

export const getAllCritiquePermission = (user) => {
  const critiqueView = user?.tpxPermissions?.includes(TPX_PERMISSION.allCritiqueView);
  const critiqueComment = user?.tpxPermissions?.includes(TPX_PERMISSION.allCritiqueComment);
  const critiqueSubmit = user?.tpxPermissions?.includes(TPX_PERMISSION.allCritiqueSubmit);
  return {
    critiqueView, 
    critiqueComment, 
    critiqueSubmit
  };
};

export const getCritiqueTemplatePermission = (user) => {
  const critiqueTemplateView = user?.tpxPermissions?.includes(TPX_PERMISSION.critiqueTemplateView);
  const critiqueTemplateCreate = user?.tpxPermissions?.includes(TPX_PERMISSION.critiqueTemplateCreate);
  const critiqueTemplateEdit = user?.tpxPermissions?.includes(TPX_PERMISSION.critiqueTemplateEdit);
  const critiqueTemplateDelete = user?.tpxPermissions?.includes(TPX_PERMISSION.critiqueTemplateDelete);
  return {
    critiqueTemplateView,
    critiqueTemplateCreate,
    critiqueTemplateEdit,
    critiqueTemplateDelete,
  };
};

export const getLockPermissions = (user) => {
  const lockPlan = user?.tpxPermissions?.includes(TPX_PERMISSION.lockPlan);
  const lockLine = user?.tpxPermissions?.includes(TPX_PERMISSION.lockLine);
  const lockDriver = user?.tpxPermissions?.includes(TPX_PERMISSION.lockDriver);
  return {
    lockPlan,
    lockLine,
    lockDriver,
  };
};
/**
 * Derive effective COA-module permissions.
 * Edit does not imply View. Sidebar and COA routes require **`coaView`**.
 *
 * @param {Array} roles
 * @returns {{ coaView: boolean, coaEdit: boolean }}
 */
export const getCoaPermissions = (user) => {
  const coaView = user?.tpxPermissions?.includes(TPX_PERMISSION.coaManagementView);
  const coaEdit = user?.tpxPermissions?.includes(TPX_PERMISSION.coaManagementEdit);
  return {
    coaView,
    coaEdit,
  };
};

/**
 * Derive effective Planning Report permissions (exports / planning reports).
 * Edit does not imply View. Sidebar and export routes require **`planningReportView`**.
 *
 * @param {Array} roles
 * @returns {{ planningReportView: boolean, planningReportEdit: boolean }}
 */
export const getPlanningReportPermissions = (user) => {

  const planningReportView = user?.tpxPermissions?.includes(TPX_PERMISSION.planningReportView);
  const planningReportEdit = user?.tpxPermissions?.includes(TPX_PERMISSION.planningReportEdit);
  return {
    planningReportView,
    planningReportEdit,
  };
};

export const getSettingsPermissions = (user) => {
  const settingsView = user?.tpxPermissions?.includes(TPX_PERMISSION.settingsView);
  const settingsEdit = user?.tpxPermissions?.includes(TPX_PERMISSION.settingsEdit);

  return {
    settingsView,
    settingsEdit,
  };
};

/**
 * Convenience: full TPX permission snapshot used by the provider.
 * Extend this as more modules are gated (COA, Planning Report, ...).
 *
 * @param {{roles?: Array}|null|undefined} user
 */
export const getTpxPermissions = (user) => {
  const roles = user?.roles || [];
  const tpxRoles = getTpxRoles(roles);
  const tpxPermissions = Array.isArray(user?.tpxPermissions) ? user.tpxPermissions : [];
  const { planView, planEdit, planCreate, planDelete } = getPlanPermissions(user);
  const { settingsView, settingsEdit } = getSettingsPermissions(user);
  const { coaView, coaEdit } = getCoaPermissions(user);
  const { planningReportView, planningReportEdit } = getPlanningReportPermissions(user);
  const { lockPlan, lockLine, lockDriver } = getLockPermissions(user);
  const versionCreate = user?.tpxPermissions?.includes(TPX_PERMISSION.versionCreate);
  const { critiqueView, critiqueComment, critiqueSubmit } = getAllCritiquePermission(user);
  const {
    critiqueTemplateView,
    critiqueTemplateCreate,
    critiqueTemplateEdit,
    critiqueTemplateDelete,
  } = getCritiqueTemplatePermission(user);
  return {
    tpxRoles,
    tpxPermissions,
    planView,
    planEdit,
    planCreate,
    planDelete,
    versionCreate,
    settingsView,
    settingsEdit,
    coaView,
    coaEdit,
    planningReportView,
    planningReportEdit,
    critiqueView,
    critiqueComment,
    critiqueSubmit,
    critiqueTemplateView,
    critiqueTemplateCreate,
    critiqueTemplateEdit,
    critiqueTemplateDelete,
    lockPlan,
    lockLine,
    lockDriver
  };
};
